import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Paper,
    Typography,
    makeStyles,
    Card,
    CardContent,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import BusinessIcon from '@material-ui/icons/Business';
import PersonIcon from '@material-ui/icons/Person';
import SettingsIcon from '@material-ui/icons/Settings';
import DescriptionIcon from '@material-ui/icons/Description';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { useFormik } from 'formik';
import * as yup from 'yup';

import { CustomTextField } from '../../components/Textfield';
import { Spinner } from '../../components/UI';
import { GetOrganisations } from '../../store/actions/settingsActionCreator';
import { getLoginUser, getPermissions } from '../../utils/helper';

// Types
interface UserData {
    id: number;
    name: string;
    organizationId: number;
    organizationName: string | null;
    contactNumber: string;
    status: string;
    role: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    userRoleList: number[];
    allowDesktopLogin: boolean;
    allowDeAppLogin: boolean;
    allowInfluencerAppLogin: boolean;
    isTestUser: boolean;
}

interface OrganizationSetting {
    id: number;
    organizationId: number;
    settingsKey: string;
    settingsValue: string;
}

interface OrganizationData {
    id: number;
    name: string;
    address: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    operatingCityId: number;
    cityName: string;
    contactNumber: string | null;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    settings: OrganizationSetting[];
    settingsMap: Record<string, string>;
    stateId: number;
    stateName: string;
    userName: string;
    vendorName: string;
    isPinCodeOrg: boolean;
}

interface RootState {
    user: UserData & { config?: Record<string, unknown> };
    settings: {
        basicDetails: OrganizationData;
        statesList: Array<{ id: number; name: string }>;
        cities: Array<{ id: number; name: string }>;
    };
}

interface InfoItemProps {
    label: string;
    value: string | number | null | undefined | boolean;
    type?: 'text' | 'status' | 'date' | 'badge';
    statusType?: 'success' | 'error' | 'warning' | 'info';
}

// Validation Schema
const userValidationSchema = yup.object({
    name: yup.string().required('Name is required'),
    contactNumber: yup
        .string()
        .matches(/^[0-9]{10}$/, 'Contact number must be 10 digits')
        .required('Contact number is required'),
});

// Styles
const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
        backgroundColor: '#fff',
        minHeight: '100vh',
        borderRadius: 12,
    },
    pageHeader: {
        marginBottom: theme.spacing(4),
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    pageTitle: {
        fontFamily: 'Nunito, sans-serif',
        fontSize: 28,
        fontWeight: 700,
        color: '#14112d',
    },
    sectionCard: {
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        marginBottom: theme.spacing(3),
        border: '1px solid #e8e8ee',
        overflow: 'hidden',
    },
    sectionHeader: {
        backgroundColor: '#43419e',
        padding: theme.spacing(2, 3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    sectionHeaderUser: {
        backgroundColor: '#43419e',
    },
    sectionHeaderOrg: {
        backgroundColor: '#3c3a8b',
    },
    sectionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    sectionIcon: {
        color: '#fff',
        fontSize: 28,
    },
    sectionTitleText: {
        fontFamily: 'Nunito, sans-serif',
        fontSize: 18,
        fontWeight: 700,
        color: '#fff',
    },
    sectionContent: {
        padding: theme.spacing(3),
        backgroundColor: '#fff',
    },
    subsectionCard: {
        backgroundColor: '#f8f9fa',
        borderRadius: 8,
        border: '1px solid #e8e8ee',
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
    },
    subsectionHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
        paddingBottom: theme.spacing(1.5),
        borderBottom: '2px solid #e8e8ee',
    },
    subsectionIcon: {
        color: '#43419e',
        fontSize: 20,
    },
    subsectionTitle: {
        fontFamily: 'Nunito, sans-serif',
        fontSize: 14,
        fontWeight: 700,
        color: '#43419e',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    infoGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    infoItem: {
        display: 'inline-flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        padding: theme.spacing(1.5, 2),
        backgroundColor: '#fff',
        borderRadius: 8,
        border: '1px solid #e8e8ee',
        minWidth: 'fit-content',
        maxWidth: 280,
    },
    infoLabel: {
        fontFamily: 'Nunito, sans-serif',
        fontSize: 11,
        fontWeight: 700,
        color: '#515f6d',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    infoValue: {
        fontFamily: 'Nunito, sans-serif',
        fontSize: 15,
        fontWeight: 600,
        color: '#14112d',
    },
    emptyValue: {
        color: '#9ca3af',
        fontStyle: 'italic',
        fontWeight: 400,
    },
    // Status Pills
    statusPill: {
        height: 28,
        fontSize: 12,
        fontWeight: 700,
        borderRadius: 14,
        padding: '0 14px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        width: 'fit-content',
    },
    statusSuccess: {
        backgroundColor: '#d1fae5',
        color: '#065f46',
    },
    statusError: {
        backgroundColor: '#fee2e2',
        color: '#991b1b',
    },
    statusWarning: {
        backgroundColor: '#fef3c7',
        color: '#92400e',
    },
    statusInfo: {
        backgroundColor: '#e8e8ee',
        color: '#43419e',
    },
    // Date Pills
    datePill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        backgroundColor: '#f3f4f6',
        color: '#374151',
        padding: '6px 14px',
        borderRadius: 20,
        fontSize: 13,
        fontWeight: 600,
        width: 'fit-content',
    },
    dateIcon: {
        fontSize: 16,
        color: '#6b7280',
    },
    // KYC Pills
    kycPill: {
        margin: theme.spacing(0.5),
        backgroundColor: '#e8e8ee',
        color: '#43419e',
        fontWeight: 700,
        width: 'fit-content',
        fontSize: 12,
        height: 28,
        borderRadius: 14,
    },
    // Settings Grid
    settingsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: theme.spacing(2),
    },
    settingCard: {
        backgroundColor: '#fff',
        borderRadius: 8,
        border: '1px solid #e8e8ee',
        padding: theme.spacing(2),
        textAlign: 'center',
        transition: 'all 0.2s ease',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(67, 65, 158, 0.15)',
            borderColor: '#43419e',
        },
    },
    settingLabel: {
        fontFamily: 'Nunito, sans-serif',
        fontSize: 11,
        fontWeight: 700,
        color: '#515f6d',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: theme.spacing(1),
    },
    settingValue: {
        fontFamily: 'Nunito, sans-serif',
        fontSize: 22,
        fontWeight: 800,
        color: '#43419e',
    },
    // Buttons
    editButton: {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        color: '#fff',
        padding: theme.spacing(1, 2.5),
        borderRadius: 25,
        fontSize: 13,
        fontWeight: 700,
        textTransform: 'none',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    cancelButton: {
        backgroundColor: '#f3f4f6',
        color: '#374151',
        padding: theme.spacing(1, 2.5),
        borderRadius: 25,
        fontSize: 13,
        fontWeight: 700,
        textTransform: 'none',
        marginRight: theme.spacing(1),
        '&:hover': {
            backgroundColor: '#e5e7eb',
        },
    },
    saveButton: {
        backgroundColor: '#10b981',
        color: '#fff',
        padding: theme.spacing(1, 2.5),
        borderRadius: 25,
        fontSize: 13,
        fontWeight: 700,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#059669',
        },
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: '#fff',
        },
    },
    timestampSection: {
        display: 'flex',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        marginTop: theme.spacing(1),
        '& $infoItem': {
            minWidth: 'auto',
        },
    },
}));

// Info Item Component
const InfoItem: React.FC<InfoItemProps> = ({
    label,
    value,
    type = 'text',
    statusType = 'info',
}) => {
    const classes = useStyles();

    const isEmpty = value === null || value === undefined || value === '';
    const displayValue = isEmpty ? 'Not provided' : String(value);

    const getStatusClass = () => {
        switch (statusType) {
            case 'success':
                return classes.statusSuccess;
            case 'error':
                return classes.statusError;
            case 'warning':
                return classes.statusWarning;
            default:
                return classes.statusInfo;
        }
    };

    const renderValue = () => {
        if (isEmpty) {
            return (
                <Typography
                    className={`${classes.infoValue} ${classes.emptyValue}`}
                >
                    Not provided
                </Typography>
            );
        }

        switch (type) {
            case 'status':
                return (
                    <Chip
                        label={displayValue}
                        size="small"
                        className={`${classes.statusPill} ${getStatusClass()}`}
                    />
                );
            case 'date':
                return (
                    <Box className={classes.datePill}>
                        <ScheduleIcon className={classes.dateIcon} />
                        {displayValue}
                    </Box>
                );
            case 'badge':
                return (
                    <Chip
                        label={displayValue}
                        size="small"
                        className={`${classes.statusPill} ${classes.statusInfo}`}
                    />
                );
            default:
                return (
                    <Typography className={classes.infoValue}>
                        {displayValue}
                    </Typography>
                );
        }
    };

    return (
        <Box className={classes.infoItem}>
            <Typography className={classes.infoLabel}>{label}</Typography>
            {renderValue()}
        </Box>
    );
};

// Main Component
export const BasicDetails: React.FC = () => {
    const classes = useStyles();
    const dispatch = useDispatch();

    // Redux State
    const user = useSelector((state: RootState) => state.user);
    const settings = useSelector((state: RootState) => state.settings);

    // Local State
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [orgInfo, setOrgInfo] = useState<OrganizationData | null>(null);

    // Formik for User Edit
    const formik = useFormik({
        initialValues: {
            name: user?.name || '',
            contactNumber: user?.contactNumber || '',
        },
        validationSchema: userValidationSchema,
        enableReinitialize: true,
        onSubmit: async () => {
            setLoading(true);
            try {
                setIsEditMode(false);
            } catch (error) {
                console.error('Error updating user:', error);
            } finally {
                setLoading(false);
            }
        },
    });

    // Fetch Organization Info
    const fetchOrganisationInfo = useCallback(() => {
        const orgId =
            settings?.basicDetails?.id || getLoginUser()?.organizationId;
        if (!orgId) return;

        setLoading(true);
        dispatch(
            GetOrganisations(
                orgId,
                (response: OrganizationData) => {
                    setOrgInfo(response);
                    setLoading(false);
                },
                () => {
                    setLoading(false);
                },
            ) as any,
        );
    }, [dispatch, settings?.basicDetails?.id]);

    useEffect(() => {
        fetchOrganisationInfo();
    }, [fetchOrganisationInfo]);

    // Handle Edit Toggle
    const handleEditClick = () => {
        setIsEditMode(true);
    };

    const handleCancelEdit = () => {
        formik.resetForm();
        setIsEditMode(false);
    };

    // Format date
    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Get KYC Required Documents
    const getKYCDocuments = (): string[] => {
        const settingsMap = orgInfo?.settingsMap || {};
        const kycDocs: string[] = [];

        if (settingsMap.AADHAR === 'kycRequired') kycDocs.push('Aadhaar');
        if (settingsMap.PANCARD === 'kycRequired') kycDocs.push('PAN Card');
        if (settingsMap.DRIVING_LICENSE === 'kycRequired')
            kycDocs.push('Driving License');
        if (settingsMap.RC === 'kycRequired') kycDocs.push('RC');

        return kycDocs;
    };

    // Get status type
    const getStatusType = (
        value: string | boolean | undefined | null,
    ): 'success' | 'error' => {
        if (
            value === true ||
            value === 'Active' ||
            value === 'APPROVED' ||
            value === 'true'
        ) {
            return 'success';
        }
        return 'error';
    };

    return (
        <Box className={classes.root}>
            <Spinner loading={loading}>
                {/* Page Header */}
                {/* <Box className={classes.pageHeader}>
                    <Typography className={classes.pageTitle}>
                        Basic Details
                    </Typography>
                </Box> */}

                {/* User Info Card */}
                <Card className={classes.sectionCard}>
                    <Box
                        className={`${classes.sectionHeader} ${classes.sectionHeaderUser}`}
                    >
                        <Box className={classes.sectionTitle}>
                            <PersonIcon className={classes.sectionIcon} />
                            <Typography className={classes.sectionTitleText}>
                                User Information
                            </Typography>
                        </Box>
                        {!isEditMode ? (
                            <>
                                {/* <Button
                                className={classes.editButton}
                                startIcon={<EditIcon />}
                                onClick={handleEditClick}
                            >
                                Edit Profile
                            </Button> */}
                            </>
                        ) : (
                            <Box>
                                <Button
                                    className={classes.cancelButton}
                                    startIcon={<CloseIcon />}
                                    onClick={handleCancelEdit}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className={classes.saveButton}
                                    startIcon={<SaveIcon />}
                                    onClick={() => formik.handleSubmit()}
                                >
                                    Save Changes
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <CardContent className={classes.sectionContent}>
                        {!isEditMode ? (
                            <>
                                <Box className={classes.infoGrid}>
                                    <InfoItem
                                        label="Full Name"
                                        value={user?.name}
                                    />
                                    <InfoItem
                                        label="Contact Number"
                                        value={user?.contactNumber}
                                    />
                                    {/* <InfoItem
                                        label="Account Status"
                                        value={user?.status}
                                        type="status"
                                        statusType={getStatusType(user?.status)}
                                    /> */}
                                    {/* <InfoItem
                                        label="Role ID"
                                        value={user?.role}
                                        type="badge"
                                    /> */}
                                    {/* <InfoItem
                                        label="Status"
                                        value={
                                            user?.isActive
                                                ? 'Active'
                                                : 'Inactive'
                                        }
                                        type="status"
                                        statusType={getStatusType(
                                            user?.isActive,
                                        )}
                                    /> */}
                                    {/* <InfoItem
                                        label="Desktop Login"
                                        value={
                                            user?.allowDesktopLogin
                                                ? 'Enabled'
                                                : 'Disabled'
                                        }
                                        type="status"
                                        statusType={
                                            user?.allowDesktopLogin
                                                ? 'success'
                                                : 'warning'
                                        }
                                    /> */}
                                    <InfoItem
                                        label="Created At"
                                        value={formatDate(user?.createdAt)}
                                        type="date"
                                    />
                                    <InfoItem
                                        label="Last Updated"
                                        value={formatDate(user?.updatedAt)}
                                        type="date"
                                    />
                                </Box>
                                {/* <Divider style={{ margin: '24px 0' }} /> */}
                                {/* <Box className={classes.timestampSection}>
                                    <InfoItem
                                        label="Created At"
                                        value={formatDate(user?.createdAt)}
                                        type="date"
                                    />
                                    <InfoItem
                                        label="Last Updated"
                                        value={formatDate(user?.updatedAt)}
                                        type="date"
                                    />
                                </Box> */}
                            </>
                        ) : (
                            <form onSubmit={formik.handleSubmit}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <CustomTextField
                                            fullWidth
                                            label="Full Name"
                                            variant="outlined"
                                            id="name"
                                            name="name"
                                            className={classes.textField}
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.name &&
                                                Boolean(formik.errors.name)
                                            }
                                            helperText={
                                                formik.touched.name &&
                                                formik.errors.name
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <CustomTextField
                                            fullWidth
                                            label="Contact Number"
                                            variant="outlined"
                                            id="contactNumber"
                                            name="contactNumber"
                                            className={classes.textField}
                                            value={formik.values.contactNumber}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.contactNumber &&
                                                Boolean(
                                                    formik.errors.contactNumber,
                                                )
                                            }
                                            helperText={
                                                formik.touched.contactNumber &&
                                                formik.errors.contactNumber
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </form>
                        )}
                    </CardContent>
                </Card>

                {/* Organization Info Card */}
                {!getPermissions('agency_hire')?.read && <Card className={classes.sectionCard}>
                    <Box
                        className={`${classes.sectionHeader} ${classes.sectionHeaderOrg}`}
                    >
                        <Box className={classes.sectionTitle}>
                            <BusinessIcon className={classes.sectionIcon} />
                            <Typography className={classes.sectionTitleText}>
                                Organization Information
                            </Typography>
                        </Box>
                    </Box>
                    <CardContent className={classes.sectionContent}>
                        {/* Basic Info */}
                        <Box className={classes.subsectionCard}>
                            <Box className={classes.subsectionHeader}>
                                <BusinessIcon
                                    className={classes.subsectionIcon}
                                />
                                <Typography className={classes.subsectionTitle}>
                                    Basic Details
                                </Typography>
                            </Box>
                            <Box className={classes.infoGrid}>
                                <InfoItem
                                    label="Organization Name"
                                    value={orgInfo?.name}
                                />
                                {/* <InfoItem
                                    label="Organization ID"
                                    value={orgInfo?.id}
                                    type="badge"
                                /> */}
                                <InfoItem
                                    label="Vendor Name"
                                    value={orgInfo?.vendorName}
                                />
                                <InfoItem
                                    label="Status"
                                    value={
                                        orgInfo?.isActive
                                            ? 'Active'
                                            : 'Inactive'
                                    }
                                    type="status"
                                    statusType={getStatusType(
                                        orgInfo?.isActive,
                                    )}
                                />
                            </Box>
                        </Box>

                        {/* Location Info */}
                        <Box className={classes.subsectionCard}>
                            <Box className={classes.subsectionHeader}>
                                <LocationOnIcon
                                    className={classes.subsectionIcon}
                                />
                                <Typography className={classes.subsectionTitle}>
                                    Location Details
                                </Typography>
                            </Box>
                            <Box className={classes.infoGrid}>
                                <InfoItem
                                    label="City"
                                    value={orgInfo?.cityName}
                                />
                                <InfoItem
                                    label="State"
                                    value={orgInfo?.stateName}
                                />
                                <InfoItem
                                    label="Address Line 1"
                                    value={orgInfo?.addressLine1}
                                />
                                <InfoItem
                                    label="Address Line 2"
                                    value={orgInfo?.addressLine2}
                                />
                            </Box>
                        </Box>

                        {/* KYC Documents */}
                        <Box className={classes.subsectionCard}>
                            <Box className={classes.subsectionHeader}>
                                <DescriptionIcon
                                    className={classes.subsectionIcon}
                                />
                                <Typography className={classes.subsectionTitle}>
                                    Required KYC Documents
                                </Typography>
                            </Box>
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                style={{ marginTop: 8 }}
                            >
                                {getKYCDocuments().length > 0 ? (
                                    getKYCDocuments().map((doc) => (
                                        <Chip
                                            key={doc}
                                            label={doc}
                                            className={classes.kycPill}
                                        />
                                    ))
                                ) : (
                                    <Typography
                                        style={{
                                            color: '#9ca3af',
                                            fontStyle: 'italic',
                                            fontSize: 14,
                                        }}
                                    >
                                        No KYC documents configured
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Organization Settings */}
                        <Box className={classes.subsectionCard}>
                            <Box className={classes.subsectionHeader}>
                                <SettingsIcon
                                    className={classes.subsectionIcon}
                                />
                                <Typography className={classes.subsectionTitle}>
                                    Organization Settings
                                </Typography>
                            </Box>
                            <Box className={classes.settingsGrid}>
                                <Box className={classes.settingCard}>
                                    <Typography
                                        className={classes.settingLabel}
                                    >
                                        KYC Nudge Days
                                    </Typography>
                                    <Typography
                                        className={classes.settingValue}
                                    >
                                        {orgInfo?.settingsMap?.kycNudge || '—'}
                                    </Typography>
                                </Box>
                                <Box className={classes.settingCard}>
                                    <Typography
                                        className={classes.settingLabel}
                                    >
                                        PDP Doc Nudge
                                    </Typography>
                                    <Typography
                                        className={classes.settingValue}
                                    >
                                        {orgInfo?.settingsMap?.pdpDocNudge ||
                                            '—'}
                                    </Typography>
                                </Box>
                                <Box className={classes.settingCard}>
                                    <Typography
                                        className={classes.settingLabel}
                                    >
                                        Profile Nudge
                                    </Typography>
                                    <Typography
                                        className={classes.settingValue}
                                    >
                                        {orgInfo?.settingsMap
                                            ?.profileDetailsNudge || '—'}
                                    </Typography>
                                </Box>
                                <Box className={classes.settingCard}>
                                    <Typography
                                        className={classes.settingLabel}
                                    >
                                        Bank Threshold
                                    </Typography>
                                    <Typography
                                        className={classes.settingValue}
                                    >
                                        {orgInfo?.settingsMap
                                            ?.bankDetailsNudgeEnabled || '—'}
                                    </Typography>
                                </Box>
                                <Box className={classes.settingCard}>
                                    <Typography
                                        className={classes.settingLabel}
                                    >
                                        Nudge Impl. Date
                                    </Typography>
                                    <Typography
                                        className={classes.settingValue}
                                        style={{ fontSize: 16 }}
                                    >
                                        {orgInfo?.settingsMap?.nudgeImplDate ||
                                            '—'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Timestamps */}
                        <Divider style={{ margin: '16px 0' }} />
                        <Box className={classes.timestampSection}>
                            <InfoItem
                                label="Created At"
                                value={formatDate(orgInfo?.createdAt)}
                                type="date"
                            />
                            <InfoItem
                                label="Last Updated"
                                value={formatDate(orgInfo?.updatedAt)}
                                type="date"
                            />
                        </Box>
                    </CardContent>
                </Card>}
            </Spinner>
        </Box>
    );
};

export default BasicDetails;
